<script setup lang="ts">
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import PMessage from 'primevue/message'
import PButton from 'primevue/button'
import PProgressSpinner from 'primevue/progressspinner'
import { useSchoolPeriodUserStore } from '@/presentation/stores/school/schoolPeriodUser.ts'
import type School from '@/core/domain/school/core/School.ts'
import { computed, onMounted, type Ref, ref, watch } from 'vue'
import { useSchoolPeriodStore } from '@/presentation/stores/school/schoolPeriod.ts'
import { storeToRefs } from 'pinia'
import {
  type SchoolPeriodUser,
  SchoolPeriodUserInput
} from '@/core/domain/school/user/SchoolPeriodUser.ts'
import PInputText from 'primevue/inputtext'
import CSchoolPeriodUsersTable from '@/presentation/components/schools/CSchoolPeriodUsersTable.vue'
import CSchoolPeriodUsersUpdate from '@/presentation/components/schools/CSchoolPeriodUsersUpdate.vue'
import CSchoolPeriodAddExistingMembers from '@/presentation/components/schools/CSchoolPeriodAddExistingMembers.vue'

import { useToast } from 'primevue/usetoast'
import { useSchoolPeriodUsersTable } from '@/presentation/composables/school/users/useSchoolPeriodUsersTable.ts'
import { useSchoolStore } from '@/presentation/stores/school/school.ts'
import { UserRole } from '@/core/domain/resources/enums/UserRole.ts'
import { useConfirm } from 'primevue/useconfirm'

interface EditUser {
  open: boolean
  user: SchoolPeriodUserInput | null
  isCreation: boolean
}

// Composables
const toast = useToast()
const confirm = useConfirm()
const schoolPeriodUserStore = useSchoolPeriodUserStore()
const schoolPeriodStore = useSchoolPeriodStore()
const schoolStore = useSchoolStore()

// Store refs
const {
  pedagogicalTeam,
  schoolPeriodUserStoreIsLoading,
  schoolPeriodUserStoreError,
  needsFreshData
} = storeToRefs(schoolPeriodUserStore)
const { selectedSchoolPeriod } = storeToRefs(schoolPeriodStore)
const { selectedSchool } = storeToRefs(schoolStore)

// Local state
const localLoading = ref(false)
const localError = ref('')

// Gestion du tableau via le composable
const { searchQuery, processUsers, resetSearch } = useSchoolPeriodUsersTable()

// Utilisateurs filtrés et triés
const processedUsers = computed(() => processUsers(pedagogicalTeam.value))
const filteredUserCount = computed(() => processedUsers.value.length)

// État d'édition
const editUser: Ref<EditUser> = ref({
  open: false,
  user: null,
  isCreation: false
})

// État pour la modal d'ajout de membres existants
const showAddExistingMembers = ref(false)

// Props
const props = defineProps<{
  school: School
}>()

// Computed
const isLoading = computed(() => localLoading.value || schoolPeriodUserStoreIsLoading.value)

const hasError = computed(
  () =>
    localError.value !== '' ||
    (schoolPeriodUserStoreError.value && schoolPeriodUserStoreError.value.hasError)
)

const errorMessage = computed(
  () =>
    localError.value ||
    (schoolPeriodUserStoreError.value?.hasError ? schoolPeriodUserStoreError.value.message : '')
)

const userCount = computed(() => pedagogicalTeam.value.length)
const hasUsers = computed(() => userCount.value > 0)
const canAddUser = computed(
  () => !isLoading.value && selectedSchoolPeriod.value !== null && props.school?.id
)

// Methods
/**
 * Charge les utilisateurs de la période scolaire
 * @param forceRefresh Force le rechargement des données même si déjà présentes
 */
async function loadUsers(forceRefresh = false) {
  if (!selectedSchoolPeriod.value || !props.school?.id) return

  // Éviter les rechargements inutiles si les données sont déjà chargées
  if (!forceRefresh && pedagogicalTeam.value.length > 0 && !needsFreshData.value) {
    return
  }

  try {
    localLoading.value = true
    localError.value = ''

    await schoolPeriodUserStore.fetchAllSchoolPeriodUsersForSchool(
      props.school.id,
      selectedSchoolPeriod.value.schoolPeriodId,
      forceRefresh
    )
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : "Erreur lors du chargement des membres de l'équipe"

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: localError.value,
      life: 5000
    })

    console.error("Erreur lors du chargement de l'équipe pédagogique:", err)
  } finally {
    localLoading.value = false
  }
}

/**
 * Prépare l'édition d'un utilisateur
 * @param user Utilisateur à modifier (ou undefined pour création)
 */
async function startUserEdit(user?: SchoolPeriodUser) {
  if (!selectedSchoolPeriod.value || !selectedSchool.value) return

  try {
    localLoading.value = true
    localError.value = ''

    if (user) {
      // Mode édition
      editUser.value.user = await schoolPeriodUserStore.prepareSchoolPeriodUserEdit(
        selectedSchool.value.id,
        selectedSchoolPeriod.value.schoolPeriodId,
        user
      )
      editUser.value.isCreation = false
    } else {
      // Mode création - Limiter aux rôles pédagogiques
      editUser.value.user = new SchoolPeriodUserInput()
      editUser.value.user.schoolPeriodId = selectedSchoolPeriod.value.schoolPeriodId
      // Par défaut on met enseignant
      editUser.value.user.roles = [UserRole.SchoolValues.TEACHER]

      editUser.value.isCreation = true
    }

    editUser.value.open = true
  } catch (err) {
    localError.value =
      err instanceof Error ? err.message : "Erreur lors de la préparation de l'édition"

    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: localError.value,
      life: 5000
    })

    console.error("Erreur lors de la préparation de l'édition:", err)
  } finally {
    localLoading.value = false
  }
}

/**
 * Dissociation de l'utilisateur avec la période scolaire.
 * @param user
 */
async function dissociateUser(user: SchoolPeriodUser) {
  confirm.require({
    message: `Voulez-vous vraiment dissocier l'utilisateur ${user.firstname} ${user.lastname} de la période scolaire ?`,
    header: "Dissociation de l'utilisateur",
    acceptLabel: 'Dissocier',
    rejectLabel: 'Annuler',
    accept: async () => {
      if (!selectedSchoolPeriod.value || !selectedSchool.value) return
      try {
        localLoading.value = true
        localError.value = ''
        await schoolPeriodUserStore.deleteSchoolPeriodUser(
          selectedSchool.value.id,
          selectedSchoolPeriod.value.schoolPeriodId,
          user.userIdentifier
        )

        schoolPeriodUserStore.showSuccessToast(
          'Dissociation utilisateur',
          `L'utilisateur ${user.firstname} ${user.lastname} a bien été dissocié de la période scolaire.`
        )
      } catch (err) {
        localError.value =
          err instanceof Error ? err.message : "Erreur lors de la dissociation de l'utilisateur"

        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: localError.value,
          life: 5000
        })
      } finally {
        localLoading.value = false
      }
    },
    reject: () => {}
  })
}

/**
 * Ferme l'éditeur d'utilisateur
 */
function closeUserEdit() {
  editUser.value.open = false
  editUser.value.user = null

  // Recharger les utilisateurs pour refléter les changements
  loadUsers(true)
}

/**
 * Gère l'ajout de membres existants
 */
function handleMembersAdded(members: any[]) {
  // Recharger les utilisateurs pour afficher les nouveaux membres ajoutés
  loadUsers(true)
}

// Watchers
watch(selectedSchoolPeriod, async (newValue) => {
  if (newValue) {
    await loadUsers(true)
  } else {
    // Réinitialiser les données lorsqu'aucune période n'est sélectionnée
    schoolPeriodUserStore.reset()
  }
})

// Lifecycle hooks
onMounted(async () => {
  // Les périodes scolaires sont déjà chargées par le router guard
  // Les données du tab sont pré-chargées par le composant parent (VSchoolsSchool)
  // On ne charge que si les données ne sont pas déjà présentes
  if (selectedSchoolPeriod.value && pedagogicalTeam.value.length === 0) {
    await loadUsers()
  }
})
</script>

<template>
  <!-- Modal d'ajout de membres existants -->
  <CSchoolPeriodAddExistingMembers
    v-model:visible="showAddExistingMembers"
    :school="school"
    :default-role="UserRole.SchoolValues.TEACHER"
    @members-added="handleMembersAdded"
  />

  <!-- Modal d'édition d'utilisateur -->
  <CSchoolPeriodUsersUpdate
    v-if="editUser.open && editUser.user"
    :is-create="editUser.isCreation"
    :school="school"
    :allowed-roles="[
      UserRole.SchoolValues.TEACHER,
      UserRole.SchoolValues.CPE,
      UserRole.SchoolValues.PERDIR
    ]"
    v-model="editUser.user"
    @close="closeUserEdit()"
  />

  <!-- Liste de l'équipe pédagogique -->
  <CBaseBlock :toggleable="false" title="Équipe pédagogique" v-else>
    <!-- Message quand aucune période n'est sélectionnée -->
    <p-message v-if="!selectedSchoolPeriod" severity="info" class="mb-4 w-full">
      Veuillez sélectionner une période scolaire pour gérer l'équipe pédagogique.
    </p-message>

    <!-- Contenu principal -->
    <div v-else class="col-span-12 flex flex-col items-start gap-4">
      <!-- Message d'erreur -->
      <p-message v-if="hasError" severity="error" :text="errorMessage" class="mb-2 w-full" />

      <!-- En-tête avec boutons d'ajout et compteur -->
      <div class="flex w-full items-center justify-between">
        <div class="flex gap-2">
          <p-button
            label="Ajouter un membre"
            icon="pi pi-plus"
            severity="primary"
            size="small"
            @click="startUserEdit()"
            :disabled="!canAddUser"
          />
          <p-button
            label="Ajouter un membre existant"
            icon="pi pi-user-plus"
            severity="secondary"
            size="small"
            @click="showAddExistingMembers = true"
            :disabled="!canAddUser"
          />
        </div>

        <div v-if="isLoading" class="flex items-center">
          <p-progress-spinner style="width: 1.5rem; height: 1.5rem" />
          <span class="ml-2">Chargement...</span>
        </div>

        <div v-else-if="hasUsers" class="flex items-center gap-2">
          <span class="text-surface-500 dark:text-surface-300 text-sm">
            {{ filteredUserCount }} membre(s) {{ searchQuery ? 'trouvé(s)' : 'au total' }}
          </span>

          <p-button
            v-if="searchQuery"
            icon="pi pi-times"
            text
            size="small"
            @click="resetSearch"
            tooltip="Effacer la recherche"
          />

          <p-button
            icon="pi pi-refresh"
            text
            size="small"
            @click="() => loadUsers(true)"
            tooltip="Actualiser"
          />
        </div>
      </div>

      <!-- Barre de recherche -->
      <div v-if="hasUsers" class="w-full">
        <span class="p-input-icon-left w-full md:w-96">
          <i class="pi pi-search" />
          <p-input-text
            v-model="searchQuery"
            placeholder="Rechercher par nom, email ou nom d'utilisateur..."
            class="w-full"
          />
        </span>
      </div>

      <!-- État vide -->
      <div
        v-if="!isLoading && !hasUsers"
        class="flex w-full flex-col items-center justify-center p-8"
      >
        <i class="pi pi-users text-surface-500 mb-4 text-4xl"></i>
        <h3 class="mt-0 mb-2">Aucun membre dans l'équipe pédagogique</h3>
        <p class="text-surface-900 text-center">
          Cette période scolaire ne contient pas encore de membres de l'équipe pédagogique.
          Commencez par ajouter un enseignant, CPE ou personnel de direction.
        </p>
      </div>

      <!-- Tableau des membres de l'équipe -->
      <CSchoolPeriodUsersTable
        v-else-if="hasUsers"
        :users="processedUsers"
        :is-loading="isLoading"
        @edit-user="startUserEdit"
        @dissociate-user="dissociateUser"
      />
    </div>
  </CBaseBlock>
</template>
